import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callAvosApi } from "../../../../utils/useAvosApiHook";

export const SCIM_KEY = "tenant-scim";

export interface ScimStatus {
    /** False when SCIM can't be set up for the tenant, see unavailable_reason. */
    available: boolean;
    /** "experience_data_tenant", or "not_enabled" when root config data_integration.scim_provisioning is off. */
    unavailable_reason: "experience_data_tenant" | "not_enabled" | null;
    enabled: boolean;
    created: string | null;
    /** Null when INGEST_URL isn't configured for this environment. */
    tenant_url: string | null;
}

export interface ScimTokenCreated extends ScimStatus {
    token: string;
}

/** SCIM status of the active tenant; never contains the token itself. */
export const useScimStatus = (tenantId: string) => useQuery({
    queryKey: [SCIM_KEY, tenantId],
    queryFn: async (): Promise<ScimStatus> => {
        const { data } = await callAvosApi("/tenant_scim");
        return data;
    },
    enabled: !!tenantId,
});

/** Create or replace the tenant's SCIM token. The response holds the token, which is shown only once. */
export const useRotateScimToken = (tenantId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (): Promise<ScimTokenCreated> => {
            const { data } = await callAvosApi("/tenant_scim/token", { method: "POST" });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [SCIM_KEY, tenantId] }),
    });
};

export const useRevokeScimToken = (tenantId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (): Promise<ScimStatus> => {
            const { data } = await callAvosApi("/tenant_scim/token", { method: "DELETE" });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [SCIM_KEY, tenantId] }),
    });
};
